import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { StatusDianEnums } from 'src/app/enum/electronic-document-enums';
import { IndicatorChartTypeEnums } from 'src/app/enum/indicator-chart-enums';
import { IndicatorChart } from 'src/app/interfaces/custom-indicators/indicator-chart.interface';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ItemChartModel } from 'src/app/models/charts/global/item-chart.model';
import { ElectronicInvoiceInterfaceChartModel } from 'src/app/models/charts/invoicing/electronic-invoice-interface-chart.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ElectronicInvoiceInterfaceDetailTableModel } from 'src/app/models/tables/invoicing/electronic-invoice-interface-detail-table.model';
import { ElectronicInvoiceInterfaceTableModel } from 'src/app/models/tables/invoicing/electronic-invoice-interface-table-model';
import { HeaderService } from 'src/app/services/header/header.service';
import { ElectronicInvoiceInterfaceService } from './../../../../services/invoicing/electronic-invoice-interface.service';

const ElectronicInvoiceInterfaceIndicatorsConstants = {
  statesSendDian: [
    { id: 1, label: 'Aprobado', color: '#198754' },
    { id: 2, label: 'Pendiente', color: '#FB923C' },
    { id: 3, label: 'Rechazado', color: '#F43F5E' }
  ],
  statesSendMail: [
    { id: 200, label: 'Enviado', color: '#198754' },
    { id: 1, label: 'Pendiente', color: '#F43F5E' }
  ]
};

@Component({
  selector: 'app-electronic-invoice-interface',
  templateUrl: './electronic-invoice-interface.component.html',
  styleUrls: ['./electronic-invoice-interface.component.scss'],
  standalone: false
})
export class ElectronicInvoiceInterfaceComponent implements OnInit {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<ElectronicInvoiceInterfaceDetailTableModel> = new PagedResponseModel<ElectronicInvoiceInterfaceDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalHeader: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<ElectronicInvoiceInterfaceTableModel> = new PagedResponseModel<ElectronicInvoiceInterfaceTableModel>();
  indicatorCharts: IndicatorChart[] = this.buildIndicatorCharts(new ElectronicInvoiceInterfaceChartModel());
  indicatorsRequestId: number = 0;
  limit: number = 10;
  loadingIndicators: boolean = false;
  nameWindow: string = 'Interfaz Factura Electrónica';
  projectName = ProjectName.projectName;
  page: number = 1;
  previousSort: { field: string; isAscendant: number };
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit',
    },
  ];

  constructor(
    private electronicInvoiceInterfaceService: ElectronicInvoiceInterfaceService,
    private headerService: HeaderService,
    private router: Router,
    private title: Title) { }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);

    const filters = localStorage.getItem('electronic-invoice-interface');
    filters ? (this.filters = JSON.parse(filters)) : [];
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getElectronicInvoiceInterfaceForGeneralConsultation(),
    ]);
  }

  buildIndicatorCharts(indicators: ElectronicInvoiceInterfaceChartModel): IndicatorChart[] {
    const { statesSendDian, statesSendMail } = ElectronicInvoiceInterfaceIndicatorsConstants;
    return [
      {
        type: IndicatorChartTypeEnums.donut,
        title: 'Estado Envío DIAN',
        labels: statesSendDian.map(state => state.label),
        values: statesSendDian.map(state => this.getIndicatorQuantity(indicators.statesSendDian, state.id)),
        colors: statesSendDian.map(state => state.color),
        total: indicators.totalSendDian
      },
      {
        type: IndicatorChartTypeEnums.donut,
        title: 'Estado Envío Correo al Cliente',
        labels: statesSendMail.map(state => state.label),
        values: statesSendMail.map(state => this.getIndicatorQuantity(indicators.statesSendMail, state.id)),
        colors: statesSendMail.map(state => state.color),
        total: indicators.totalSendMail
      }
    ];
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(
        this.electronicInvoiceInterfaceService.exportDetailConsultation(
          this.filters,
          this.columnSort,
          this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Interfaz factura electronica Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(
        'Error al exportar la interfaz de factura electrónica detalle.',
        error
      );
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(
        this.electronicInvoiceInterfaceService.exportGeneralConsultation(
          this.filters,
          this.columnSort,
          this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Interfaz factura electronica.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar la interfaz de factura electrónica.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(
          this.electronicInvoiceInterfaceService.getColumnsForDetailConsultation()
        );
      }
    } catch (error) {
      console.error(
        'Error al buscar las columnas para la consulta detalle.',
        error
      );
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(
          this.electronicInvoiceInterfaceService.getColumnsForGeneralConsultation()
        );
      }
    } catch (error) {
      console.error(
        'Error al buscar las columnas para la consulta general.',
        error
      );
    }
  }

  async getElectronicInvoiceInterfaceForGeneralConsultation() {
    try {
      this.generalData = await firstValueFrom(
        this.electronicInvoiceInterfaceService.getElectronicInvoiceInterfaceForGeneralConsultation(
          this.page,
          this.limit,
          this.columnSort,
          this.ascendant,
          this.filters
        )
      );
    } catch (error) {
      console.error('Error al buscar las interfaces de facturación electrónica.', error);
    }
  }

  async getElectronicInvoiceInterfaceDetailsForDetailConsultation() {
    try {
      this.detailData = await firstValueFrom(
        this.electronicInvoiceInterfaceService.getElectronicInvoiceInterfaceDetailsForDetailConsultation(
          this.page,
          this.limit,
          this.columnSort,
          this.ascendant,
          this.filters
        ));
    } catch (error) {
      console.error('Error al buscar la consulta detalle.', error);
    }
  }

  async getIndicatorsForGeneralConsultation() {
    // Descarta respuestas de solicitudes anteriores que lleguen después de la última
    const requestId = ++this.indicatorsRequestId;
    this.loadingIndicators = true;
    try {
      const indicators = await firstValueFrom(this.electronicInvoiceInterfaceService.getIndicatorsForDetailConsultation(this.filters));
      if (requestId === this.indicatorsRequestId) {
        this.indicatorCharts = this.buildIndicatorCharts(indicators);
      }
    } catch (error) {
      console.error('Error al buscar los indicadores de las interfaces de facturación electrónica.', error);
    } finally {
      if (requestId === this.indicatorsRequestId)
        this.loadingIndicators = false;
    }
  }

  getIndicatorQuantity(items: ItemChartModel[], id: number): number {
    return items?.find(item => item.id === id)?.quantity ?? 0;
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length =
      this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length =
      this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getElectronicInvoiceInterfaceForGeneralConsultation(),
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10,
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getElectronicInvoiceInterfaceDetailsForDetailConsultation(),
          this.getIndicatorsForGeneralConsultation()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10,
        };
        break;
    }
  }

  newElectronicInvoiceInterfaceEntry() {
    this.router.navigate(['/electronic-invoice-interface/register']);
  }

  onAction(event: {
    action: string;
    item?: ElectronicInvoiceInterfaceTableModel;
    selectedItems?: number[];
  }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([
        `/electronic-invoice-interface/register/${event.item.electronicInvoiceInterfaceId}`,
      ]);
    }
  }

  async onPageChange(event: {
    page: number;
    pageSize: number;
    sortField: string | undefined;
    sortOrder: number;
  }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getElectronicInvoiceInterfaceForGeneralConsultation();
        break;
      case 1:
        await this.getElectronicInvoiceInterfaceDetailsForDetailConsultation();
        break;
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);

    // Procesar cada filtro
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  rejectedDianRowClass = (item: ElectronicInvoiceInterfaceDetailTableModel): string =>
    item.validationDian === StatusDianEnums.rechazado ? 'danger-row' : '';

  private removeDuplicates(filters: any[]): any[] {
    return filters.filter(
      (value, index, self) =>
        index ===
        self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  private processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (
      valueFilter === null ||
      valueFilter === undefined ||
      valueFilter === '' ||
      valueFilter === 0
    ) {
      this.filters = this.filters.filter(
        (f) => f.propertyName !== propertyName
      );
    } else {
      const existingFilter = this.filters.find(
        (f) => f.propertyName === propertyName
      );
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }
}
